import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Camera, Send, CheckCircle, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/App';

export default function TestimonialSubmitForm({ onSubmitted }) {
  const [canSubmit, setCanSubmit] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [daysCompleted, setDaysCompleted] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [testimonialText, setTestimonialText] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  useEffect(() => {
    checkCanSubmit();
  }, []);

  const checkCanSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/testimonials/can-submit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanSubmit(response.data.can_submit);
      setAlreadySubmitted(response.data.already_submitted);
      setDaysCompleted(response.data.days_completed);
    } catch (error) {
      console.error('Error checking testimonial eligibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/testimonials/upload-photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setPhotoUrl(response.data.url);
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!testimonialText.trim()) {
      toast.error('Please write a testimonial');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/testimonials/submit`, {
        rating,
        testimonial_text: testimonialText,
        photo_url: photoUrl || null,
        display_city: city || null,
        display_state: state || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Thank you! Your testimonial has been submitted for review.');
      setAlreadySubmitted(true);
      if (onSubmitted) onSubmitted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Testimonial Submitted!</h3>
        <p className="text-gray-600">
          Thank you for sharing your experience. Your testimonial is being reviewed and will appear on our site soon.
        </p>
      </div>
    );
  }

  if (!canSubmit) {
    return (
      <div className="bg-gray-50 rounded-2xl shadow-lg p-8 text-center">
        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <Star className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Share Your Experience</h3>
        <p className="text-gray-600 mb-4">
          Complete more of the program to leave a testimonial.
        </p>
        <div className="bg-white rounded-full px-4 py-2 inline-block">
          <span className="text-gray-600">{daysCompleted} / 70 days completed</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">Share Your Journey</h3>
        <p className="text-gray-600">
          Your experience could inspire other women to start their transformation!
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Star Rating */}
        <div>
          <Label className="block text-gray-700 mb-2">Your Rating</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`w-8 h-8 ${
                    star <= (hoverRating || rating)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Testimonial Text */}
        <div>
          <Label htmlFor="testimonial" className="block text-gray-700 mb-2">
            Your Testimonial
          </Label>
          <Textarea
            id="testimonial"
            value={testimonialText}
            onChange={(e) => setTestimonialText(e.target.value)}
            placeholder="Share how Radiant 40 has impacted your life..."
            className="min-h-[120px]"
            maxLength={500}
          />
          <p className="text-sm text-gray-500 mt-1">{testimonialText.length}/500 characters</p>
        </div>

        {/* Photo Upload */}
        <div>
          <Label className="block text-gray-700 mb-2">Your Photo (Optional)</Label>
          <div className="flex items-center gap-4">
            {photoUrl ? (
              <div className="relative">
                <img
                  src={`${API.replace('/api', '')}${photoUrl}`}
                  alt="Your photo"
                  className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                />
                <button
                  type="button"
                  onClick={() => setPhotoUrl('')}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ×
                </button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <div className="w-20 h-20 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-primary transition-colors">
                  {uploadingPhoto ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                  ) : (
                    <Camera className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
              </label>
            )}
            <p className="text-sm text-gray-500">
              Adding a photo helps others connect with your story
            </p>
          </div>
        </div>

        {/* Location (Optional) */}
        <div>
          <Label className="block text-gray-700 mb-2">
            <MapPin className="w-4 h-4 inline mr-1" />
            Location (Optional)
          </Label>
          <div className="flex gap-3">
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="flex-1"
            />
            <Input
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="State"
              className="w-24"
            />
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={submitting || !testimonialText.trim()}
          className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg rounded-full"
        >
          {submitting ? (
            'Submitting...'
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Submit Testimonial
            </>
          )}
        </Button>

        <p className="text-sm text-gray-500 text-center">
          Your testimonial will be reviewed before appearing publicly.
          We may display your first name and location.
        </p>
      </form>
    </div>
  );
}
